import React, { useState, useEffect, useCallback, FormEvent, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import * as HttpStatus from "http-status-codes";
import validator from "validator";
import { Paper, Box, TextField, FormControl, InputLabel, MenuItem, Select, Typography, Button, RadioGroup, FormControlLabel, Radio, FormHelperText, IconButton } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Navigate } from "react-router";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { CustomDialog } from "./CustomDialog";
import { addNotification } from "../utils/NotificationHandler";
import { Channel, channelTypes } from "../models/ChannelModel";
import { acrAPI } from "../utils/api";
import { auto } from "@popperjs/core";
import { height } from "@mui/system";
import { MyTextField } from "utils/theme";
import styled from "@emotion/styled";

interface IChannelInformationProps {
  channelId: Number;
  channelTypeInfo: String;                // Display button qualityIndexEnableFromUI depending on channelType
  qualityIndexEnableFromLicense: Boolean; // Display button qualityIndexEnableFromUI depending on License
}


const MAX_WATERMARK_ID_SNAP = 65535;

export function ChannelInformation(channelInformationProps: IChannelInformationProps): React.ReactElement {
  const { t } = useTranslation();

  const [channel, setChannel] = useState<Channel>();
  let isUpdate: boolean = false;
  if (channelInformationProps.channelId && typeof channelInformationProps.channelId === "number") {
    if (channelInformationProps.channelId >= 0) {
      isUpdate = true;
    }
  }

  // Channel information
  const [channelName, setChannelName] = useState("");
  const [channelGroup, setChannelGroup] = useState("");
  const [channelDetails, setChannelDetails] = useState("");
  const [channelType, setChannelType] = useState("");
  const [channelEnable, setChannelEnable] = useState(true);
  const [url, setUrl] = useState("");
  const [watermarkIds, setWatermarkIds] = useState<Array<number>>(new Array<number>());
  const [qualityIndexEnableFromUI, setQualityIndexEnableFromUI] = useState(true);
  const [newWatermarkId, setNewWatermarkId] = useState(0);
  const [selectedWatermarkIds, setSelectedWatermarkIds] = useState([0]);
  const [programNumber, setProgramNumber] = useState("");
  const [pid, setPid] = useState("");
  const [mediaLogLevel, setMediaLogLevel] = useState(1);

  const [channelLoaded, setChannelLoaded] = useState(false);
  const [deviceLoaded, setDeviceLoaded] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [devices, setDevices] = useState<string[]>([]);
  const [devicesDisplay, setDevicesDisplay] = useState<string[]>([]);
  const [selectedDevice, setSelectedDevice] = useState("");

  const getAvailableDevices = async () => {
    try {
      let url = '/device/available';
      if(isUpdate && deviceName)
        url += '?deviceName=' + encodeURIComponent(deviceName);
      const response = await axios.get(url);
      const devicesData = response.data.devices || [];
      const devicesName = devicesData.map((device: any) => device.name);
      setDeviceLoaded(true);
      setDevices(devicesName);
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };
 
  useEffect(() => {
    if(isUpdate && channelLoaded) {
      getAvailableDevices();
    }
  }, [channelLoaded]);

  const getChannel = useCallback(async () => {
    await axios
      .get(`/channel/${channelInformationProps.channelId}`)
      .then((response) => {
        const tempChannel: Channel = response.data;
        if (tempChannel) {
          setChannelName(tempChannel.name);
          setChannelGroup(tempChannel.group);
          setChannelDetails(tempChannel.details);
          setUrl(tempChannel.url);
          setChannelEnable(tempChannel.channelEnable);
          setChannelType(tempChannel.channelType);
          if (tempChannel.deviceName)
            setDeviceName(tempChannel.deviceName);
          //console.log(`getChannel deviceName:${tempChannel.deviceName}`); // TODO debug only
          setWatermarkIds(tempChannel.watermarkIds);
          setQualityIndexEnableFromUI(tempChannel.qualityIndexEnableFromUI);
          setProgramNumber(tempChannel.programNumber);
          setPid(tempChannel.pid);
          setMediaLogLevel(tempChannel.mediaLogLevel);
          setChannelLoaded(true);
        }
        setChannel(tempChannel);
      })
      .catch((e) => console.log("Error while fetching channel:", e));
  }, [channelInformationProps.channelId]);

  useEffect(() => {
    if (isUpdate) {
      getChannel();
    }
    else {
      getAvailableDevices();
    }
  }, [isUpdate]);

  useEffect(() => {
    if ((deviceLoaded && isUpdate && channelLoaded) || (deviceLoaded && !isUpdate)) {
      const mergedDevices = devices;  //deviceName ? [deviceName, ...devices] : devices;

      if (mergedDevices.length > 0) {
        mergedDevices.unshift(t("unselect device"));
      }
      else {
        mergedDevices.unshift(t("no device available"));
      }
      setDevicesDisplay(mergedDevices);
      if (deviceName) {
        setSelectedDevice(deviceName);
      } else {
        setSelectedDevice(mergedDevices[0]);
      }

      if (deviceName) {
        devicesDisplay.unshift(deviceName);
      }
    }
  }, [deviceLoaded, channelLoaded]);

  const getSelectedDeviceName = () => {
    if (selectedDevice === t("no device available") || selectedDevice === t("unselect device")) {
      return "";
    }
    return selectedDevice;
  };


  const [open, setOpen] = useState(false);
  const openCustomDialog = () => setOpen(true);
  const [redirectToMosaic, setRedirectToMosaic] = useState(false);

  const close = () => setOpen(false);

  function deleteChannel(): void {
    if (channelInformationProps.channelId) {
      axios
        .delete(`/channel/${channelInformationProps.channelId}`)
        .then((res) => {
          if (res && res.status === HttpStatus.OK) {
            //Removing channel ffrom context for an immmediate mosaic refresh
            const tmpChannels = acrAPI.channelsSubject
              .getValue()
              .filter((channel) => channel.id !== channelInformationProps.channelId);
            acrAPI.channelsSubject.next(tmpChannels);
            setRedirectToMosaic(true);
            addNotification(t("channel successfully deleted"));
          } else {
            addNotification(t("failed to delete channel"), "error");
            close();
          }
        })
        .catch((e) => {
          addNotification(t("failed to delete channel"), "error");
          close();
          console.log("Failed to delete channel", e);
        });
    }
  }

  const handleNewWatermarkId = (event: ChangeEvent<HTMLInputElement>) => {
    const enteredWatermarkId = Number(event.target.value);
    if (enteredWatermarkId > MAX_WATERMARK_ID_SNAP) {
      setNewWatermarkId(MAX_WATERMARK_ID_SNAP);
    } else if (!Number.isInteger(enteredWatermarkId)) {
      setNewWatermarkId(enteredWatermarkId | 0);
    } else if (enteredWatermarkId < 1) {
      setNewWatermarkId(1);
    } else {
      setNewWatermarkId(enteredWatermarkId);
    }
  };

  const addWatermarkId = () => {
    if (newWatermarkId) {
      const ids = watermarkIds;
      if (ids && watermarkIds && ids.indexOf(newWatermarkId) === -1) {
        setWatermarkIds([...watermarkIds, newWatermarkId]);
      }
    }
  };

  const isCurrentUrlValid = () => {
    let valid: boolean = validator.isURL(url, {
      protocols: ["file", "http", "https", "rtsp", "sdp", "udp"],
      require_protocol: true,
      require_valid_protocol: true,
      require_host: false,
      require_tld: false,
    });
    if (valid) {
      return true;
    }
    return false;
  };

  function isCurrentUrlValidOrEmpty(): boolean {
    if (url?.length === 0) {
      return true;
    }
    return isCurrentUrlValid();
  }

  const isProgramNumberValid = () => {
    if (programNumber.length > 5) {
      return false;
    }
    return true;
  };

  const isPidValid = () => {
    if (pid.length > 8) {
      return false;
    }
    return true;
  };

  const isChannelChange = () => {
    let res: boolean = false;
    if (channel) {
      res =
        channelName !== channel.name ||
        channelGroup !== channel.group ||
        channelDetails !== channel.details ||
        url !== channel.url ||
        channelEnable !== channel.channelEnable ||
        channelType !== channel.channelType ||
        ((channel.deviceName !== "") && (selectedDevice !== channel.deviceName)) ||
        watermarkIds !== channel.watermarkIds ||
        qualityIndexEnableFromUI !== channel.qualityIndexEnableFromUI ||
        programNumber !== channel.programNumber ||
        pid !== channel.pid ||
        mediaLogLevel !== channel.mediaLogLevel;
    }
    res = res && isValidUrlOrDevice();
    return res;
  };

  function isDeviceSelected(): boolean {
    return (getSelectedDeviceName() !== "");
  }

  function isValidUrlOrDevice(): boolean {
    return isCurrentUrlValid() || isDeviceSelected();
  }

  const isValidChannel = () => {
    return (channelName.length !== 0) && (channelType.length !== 0) && isValidUrlOrDevice();
  };

  function isUrlRequired(): boolean {
    return !isDeviceSelected();
  }

  function isDeviceRequired(): boolean {
    return (url === '') && (programNumber === '') && (pid === '');
  }

  const handleRemoveSelected = () => {
    const newWatermarkId = watermarkIds.filter((watermarkId) => selectedWatermarkIds.indexOf(watermarkId) === -1);
    setWatermarkIds(newWatermarkId);
    setSelectedWatermarkIds([0]);
  };

  const handleChannelEnable = () => {
    setChannelEnable(!channelEnable);
  };

  const handleChangeType = (event: SelectChangeEvent<string>) => {
    const value: string = event.target.value as string;
    setChannelType(value);
  };

  const handleChannelName = (event: ChangeEvent<HTMLInputElement>) => {
    setChannelName(event.target.value);
  };

  const handleChannelGroup = (event: ChangeEvent<HTMLInputElement>) => {
    setChannelGroup(event.target.value);
  };

  const handleChannelDetails = (event: ChangeEvent<HTMLInputElement>) => {
    setChannelDetails(event.target.value);
  };

  const handleUrl = (event: ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
  };

  const handleProgramNumber = (event: ChangeEvent<HTMLInputElement>) => {
    setProgramNumber(event.target.value);
  };

  const handlePid = (event: ChangeEvent<HTMLInputElement>) => {
    setPid(event.target.value);
  };

  const handleMediaLogLevel = (event: SelectChangeEvent<number>) => {
    const value: number = event.target.value as number;
    setMediaLogLevel(value);
  };

  const handleQualityIndexEnableFromUI = () => {
    setQualityIndexEnableFromUI(!qualityIndexEnableFromUI);
  };

  const handleCancelOrDelete = () => {
    isUpdate ? openCustomDialog() : setRedirectToMosaic(true);
  };

  const handleDelete = () => {
    openCustomDialog();
  };

  const handleCancel = () => {
    setRedirectToMosaic(true);
  };

  const handleSelect = (event: React.MouseEvent) => {
    const value = Number(event.currentTarget.textContent);
    if (selectedWatermarkIds.indexOf(value) > -1) {
      const newSelection: number[] = selectedWatermarkIds.filter((id) => id !== value);
      setSelectedWatermarkIds(newSelection);
    } else {
      const newSelection = [...selectedWatermarkIds, value];
      setSelectedWatermarkIds(newSelection);
    }
  };

  const inputLabel = React.useRef<HTMLLabelElement>(null);

  const handleCreateSubmit = async (event: FormEvent) => {
    event.preventDefault();
    let newChannel = {
      name: channelName.toString(),
      group: channelGroup.toString(),
      details: channelDetails.toString(),
      url: url,
      channelEnable: channelEnable,
      channelType: channelType.toString(),
      deviceName: getSelectedDeviceName(),
      watermarkIds: watermarkIds,
      qualityIndexEnableFromUI: qualityIndexEnableFromUI,
      programNumber: programNumber,
      pid: pid,
      mediaLogLevel: mediaLogLevel,
    };
    await axios
      .post(`/channel`, newChannel)
      .then((res) => {
        if (res && res.status === HttpStatus.OK) {
          const createdChannel = new Channel();
          createdChannel.name = newChannel.name;
          createdChannel.group = newChannel.group;
          createdChannel.details = newChannel.details;
          createdChannel.channelEnable = newChannel.channelEnable;
          createdChannel.channelType = newChannel.channelType;
          createdChannel.deviceName = newChannel.deviceName;
          createdChannel.qualityIndexEnableFromUI = newChannel.qualityIndexEnableFromUI;
          createdChannel.programNumber = newChannel.programNumber;
          createdChannel.pid = newChannel.pid;
          createdChannel.mediaLogLevel = newChannel.mediaLogLevel;
          const tmpChannels = acrAPI.channelsSubject.getValue();
          tmpChannels.push(createdChannel);
          acrAPI.channelsSubject.next(tmpChannels.sort((a: Channel, b: Channel) => a.name.localeCompare(b.name)));
          addNotification(t("channel successfully created"));
          setRedirectToMosaic(true);
        } else {
          addNotification(t("failed to create channel"), "error");
        }
      })
      .catch((e) => {
        console.log("failed to create channel", e);
        addNotification(t("failed to create channel"), "error");
      });
    try {
      await acrAPI.getStatus();
    } catch (e: any) {
      console.log(`Error while getting status (init): ${e?.status} (${e?.statusText})`);
    }
  };

  const handleUpdateSubmit = async (event: FormEvent) => {
    event.preventDefault();
    let newChannel = {
      name: channelName.toString(),
      group: channelGroup?.toString(),
      details: channelDetails?.toString(),
      url: url,
      channelEnable: channelEnable,
      channelType: channelType,
      deviceName: getSelectedDeviceName(),
      watermarkIds: watermarkIds,
      qualityIndexEnableFromUI: qualityIndexEnableFromUI,
      programNumber: programNumber,
      pid: pid,
      mediaLogLevel: mediaLogLevel,
    };

    await axios
      .put(`/channel/${channel && channel.id}`, newChannel)
      .then((res) => {
        if (res && res.status === HttpStatus.OK) {
          addNotification(t("channel successfully updated"));
          const tmpChannels = acrAPI.channelsSubject.getValue().map((tmpChannel) => {
            if (channel && tmpChannel.id !== channel.id) {
              return new Channel(tmpChannel);
            }
            tmpChannel.channelEnable = newChannel.channelEnable;
            tmpChannel.channelType = newChannel.channelType;
            tmpChannel.deviceName = newChannel.deviceName;
            tmpChannel.name = newChannel.name;
            tmpChannel.group = newChannel.group;
            tmpChannel.details = newChannel.details;
            tmpChannel.url = newChannel.url;
            tmpChannel.watermarkIds = newChannel.watermarkIds;
            tmpChannel.qualityIndexEnableFromUI = newChannel.qualityIndexEnableFromUI;
            tmpChannel.programNumber = newChannel.programNumber;
            tmpChannel.pid = newChannel.pid;
            tmpChannel.mediaLogLevel = newChannel.mediaLogLevel;
            return tmpChannel;
          });
          acrAPI.channelsSubject.next(tmpChannels.sort((a: Channel, b: Channel) => a.name.localeCompare(b.name)));
          setRedirectToMosaic(true);
        } else {
          addNotification(t("failed to update channel"), "error");
          close();
        }
      })
      .catch((e) => {
        console.log("failed to update channel", e);
        addNotification(t("failed to update channel"), "error");
      });
  };

  const isCurrentChannelNameValid = () => channelName.length > 50;

  return (
    <Paper elevation={4}>
      {redirectToMosaic && <Navigate to="/mosaic" />}
      {isUpdate && (
        <CustomDialog
          title={t("deleteConfirmationTitle")}
          message={t("deleteConfirmation", {
            //TODO REFACTOR THIS!
            channelName: channelName,
          })}
          action={deleteChannel}
          open={open}
          disableAction={true}
          handleClose={close}
        />
      )}
      <Box marginLeft={2} marginRight={2} p={2}>
        <form onSubmit={isUpdate ? handleUpdateSubmit : handleCreateSubmit}>

          {/* Main grid */}
          <Grid container spacing={2} direction="row" justifyContent="center" alignItems="flex-start">
            <Grid item xs={4}>  {/* Left columun */}
              {/* Description */}
              <Box display="flex" component="fieldset" flexDirection="column" sx={{ borderRadius: 2}}>
                <legend>{t("description")}</legend>
                <TextField
                  margin="normal"
                  fullWidth
                  required
                  error={isCurrentChannelNameValid()}
                  helperText={t("channel name limitation")}
                  id="channelName"
                  label={t("channel name")}
                  name="channelName"
                  value={channelName}
                  variant="standard"
                  inputProps={{ maxLength: "50" }}
                  onChange={handleChannelName}
                  autoComplete="channelName"
                  autoFocus
                  data-cy="channel-information-name"
                />
                <Grid container spacing={2}>
                  {/* Type + free space */}
                  <Grid item xs={4}>
                    <InputLabel required id="select-type-label">{t("channel type")}</InputLabel>
                    <Select
                      labelId="select-type-label"
                      label={t("channel type")}
                      sx={{ width: "100%" }}
                      required
                      onChange={handleChangeType}
                      data-cy="channel-information-name-list"
                      name="channelTypeSelect"
                      value={channelType}
                      variant="standard"
                    >
                      {channelTypes.values.map((type: any, index: number) => (
                        <MenuItem key={`channelType${type.id}`} value={type.id} data-cy={`channel-information-name-option-${index}`}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
                {/* Group */}
                <TextField
                  margin="normal"
                  fullWidth
                  helperText={t("channel group limitation")}
                  id="channelGroup"
                  label={t("channel group")}
                  name="channelGroup"
                  value={channelGroup}
                  variant="standard"
                  inputProps={{ maxLength: "50" }}
                  onChange={handleChannelGroup}
                  autoComplete="channelGroup"
                  data-cy="channel-information-group"
                />
                {/* Details */}
                <TextField
                  margin="normal"
                  fullWidth
                  helperText={t("channel details limitation")}
                  id="channelDetails"
                  label={t("channel details")}
                  name="channelDetails"
                  value={channelDetails}
                  variant="standard"
                  inputProps={{ maxLength: "100" }}
                  onChange={handleChannelDetails}
                  autoComplete="channelDetails"
                  data-cy="channel-information-details"
                />
              </Box> {/* END Description */}
              
              <Box sx={{ marginTop: "1em" }}></Box> {/* space */}
               {/* Others */}
               <Box display="flex" component="fieldset" flexDirection="column" sx={{ borderRadius: 2, padding: "1em" }}>
               <legend>{t("others")}</legend>
               <Grid container spacing={2}>
                  {/* Type + free space */}
                  <Grid item xs={4}>                  
                  <Box> {/* Media log level */}
                    <InputLabel required id="select-media-log-level-label">{t("media log level")}</InputLabel>
                    <Select
                      onChange={handleMediaLogLevel}
                      name="mediaLogLevel"
                      sx={{ width: "100%" }}
                      value={mediaLogLevel}
                      variant="standard"
                      required
                    >
                      <MenuItem value={"1"} selected>INFO</MenuItem>
                      <MenuItem value={"2"}>WARNING</MenuItem>
                      <MenuItem value={"3"}>ERROR</MenuItem>
                    </Select>
                  </Box> {/* END Media log level*/}
                  </Grid>
                  </Grid>
                <Box sx={{ marginTop: "1em", width: "10em" }}> {/* Enable/disable */}
                  <Box display="flex" margin-up="big" margin-bottom="normal">
                    <FormControl variant="standard">
                      <Typography variant="body2" data-cy="channel-information-channel-active">
                        {t("channel enable")}
                      </Typography>
                      <RadioGroup row defaultValue="on"
                        value=""
                        aria-labelledby="radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        onChange={handleChannelEnable} >
                        <FormControlLabel value="on" control={<Radio checked={channelEnable} size="medium" />} label="On" />
                        <FormControlLabel value="off" control={<Radio checked={!channelEnable} size="medium" />} label="Off" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box> {/* END Enable/disable */}
              </Box>  {/* END Others */}

            </Grid>


            <Grid item xs={4}> {/* Middle column */}
            {/* Inputs */}            
              <Box display="flex" component="fieldset" flexDirection="column" sx={{ borderRadius: 2, padding: "1em", backgroundColor: isUrlRequired() ? "inherit" : "#f0f0f0" }}
              >
                <legend>{t("IP input")}</legend>
                <TextField
                  margin="normal"
                  variant="standard"
                  required={isUrlRequired()}
                  disabled={!isUrlRequired()}
                  fullWidth
                  error={!isCurrentUrlValidOrEmpty()}
                  helperText={t("channel url limitation")}
                  id="idUrl"
                  label={t("url")}
                  name="url"
                  value={url}
                  autoComplete="url"
                  onChange={handleUrl}
                  data-cy="channel-information-url"
                />
                <TextField
                  margin="normal"
                  variant="standard"
                  fullWidth
                  error={!isProgramNumberValid()}
                  disabled={!isUrlRequired()}
                  id="idProgramNumber"
                  label={t("program number")}
                  name="programNumber"
                  value={programNumber}
                  autoComplete="programNumber"
                  onChange={handleProgramNumber}
                  data-cy="channel-information-program-number"
                />
                <TextField
                  margin="normal"
                  variant="standard"
                  fullWidth
                  error={!isPidValid()}
                  disabled={!isUrlRequired()}
                  id="idPid"
                  label={t("pid")}
                  name="pid"
                  value={pid}
                  autoComplete="pid"
                  onChange={handlePid}
                  data-cy="channel-information-pid"
                />
              </Box>

              <Box sx={{ marginTop: "0.5em", width: "100%" }}>{t("or")}</Box>

              <Box display="flex" component="fieldset" flexDirection="column" sx={{ borderRadius: 2, padding: "1em", backgroundColor: isDeviceRequired() ? "inherit" : "#f0f0f0" }}
              >
                <legend>{t("physical input")}</legend>
                <InputLabel required id="select-device-label">{t("device")}</InputLabel>
                <Select
                  labelId="select-device-label"
                  label={t("device")}
                  sx={{ width: "100%" }}
                  required={isDeviceRequired()}
                  disabled={!isDeviceRequired()}
                  onChange={(event) => setSelectedDevice(event.target.value)}
                  data-cy="channel-device-name-list"
                  name="deviceNameSelect"
                  value={selectedDevice}
                  variant="standard"
                >
                  {devicesDisplay.map((device) => (
                    <MenuItem key={device} value={device}>{device}</MenuItem>)
                  )}
                </Select>
              </Box> {/* END Inputs */}
            </Grid> {/* END Middle column */}


            <Grid item xs={4}> {/* Right column */}                      
              {/* Expected payload */}
              <Box display="flex" component="fieldset" sx={{ borderRadius: 2 }} flexDirection="column">
                <legend>{t("snap watermarking")}</legend>

                <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-start">
                  <Grid item xs={8}> {/* LEFT column */}
                    <Grid container spacing={4} direction="row" justifyContent="center" alignItems="flex-start">
                      <Grid item xs={10}>  {/* Input wmk */}
                        {/*<Typography variant="body2">{t("expected snap ids")}</Typography>*/}
                        <TextField
                          margin="dense"
                          label={t("expected snap ids")}
                          variant="standard"
                          fullWidth
                          type="number"
                          value={newWatermarkId}
                          id="newWatermarkId"
                          name="newWatermarkId"
                          onChange={handleNewWatermarkId}
                          data-cy="channel-information-expected-wmk-id-input"
                        />
                        {/* quality index */}
                        {channelInformationProps.qualityIndexEnableFromLicense && channelInformationProps?.channelTypeInfo !== "am" && (
                          <Box display="flex" margin-up="big" margin-bottom="normal" sx={{ marginTop: "2em" }}>
                            <FormControl variant="standard">
                              <Typography variant="body2" data-cy="channel-information-quality-index-enable-fromUI">
                                {t("quality index enable")}
                              </Typography>
                              <RadioGroup row defaultValue="on"
                                value=""
                                aria-labelledby="radio-buttons-group"
                                name="controlled-radio-buttons-group"
                                onChange={handleQualityIndexEnableFromUI} >
                                <FormControlLabel value="on" control={<Radio checked={qualityIndexEnableFromUI} size="medium" color="primary" />} label="On" />
                                <FormControlLabel value="off" control={<Radio checked={!qualityIndexEnableFromUI} size="medium" color="primary" />} label="Off" />
                              </RadioGroup>
                            </FormControl>
                          </Box>
                        )} {/* END quality index */}
                      </Grid> {/* END Input wmk */}
                      <Grid item xs={2}> {/* Buttons */}
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <IconButton style={{marginTop: '6px', marginRight: '6px', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.6)', width: 'fit-content' }} onClick={addWatermarkId}>
                            <AddIcon style={{ fontSize: '24px', color: 'black' }} />
                          </IconButton>
                          <IconButton style={{marginTop: '6px', marginRight: '6px', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.6)', width: 'fit-content' }} onClick={handleRemoveSelected}>
                            <RemoveIcon style={{ fontSize: '24px', color: 'black' }}/>
                          </IconButton>
                        </Box>
                      </Grid> {/* END Buttons */}
                    </Grid>
                  </Grid>  {/* END LEFT column */}
                  <Grid item xs={4}> {/* wmk list */}
                    <Select native multiple value={selectedWatermarkIds}
                          sx={{ width: "100%", height: "100%", alignItems: "start", padding: "0px", }}
                          inputProps={{ id: "select-multiple-native", size: 31 }}
                        >
                          {watermarkIds &&
                            watermarkIds.map((watermarkId) => (
                              <option
                                onClick={handleSelect}
                                style={{ width: "3em" }}
                                vertical-align="top"
                                value={watermarkId}
                                key={`watermarkOption${watermarkId}`}
                              >
                                {watermarkId}
                              </option>
                            ))}
                        </Select>
                  </Grid> {/* END wmk list */}
                </Grid>
                                
              </Box>
              {/* END Expected payload */}


            </Grid> {/* END Right column */}
          </Grid> {/* END Main grid */}

          <Box sx={{ marginTop: "3em" }}></Box> {/* space */}

          <Grid container spacing={4} direction="row" justifyContent="flex-start" alignItems="center">  {/* Row buttons */}
            <Grid item xs={4}></Grid>
            <Grid item xs={1}>
              {isUpdate && (
                <Button color="secondary" variant="contained" onClick={handleDelete}>
                  {t("delete")}
                </Button>
              )}
            </Grid>
            <Grid item xs={3}></Grid>
            <Grid item xs={1}>
              <Button color="info" variant="contained" onClick={handleCancel}>
                {t("cancel")}
              </Button>
            </Grid>
            <Grid item xs={1}>
              {isUpdate ? (
                <Button type="submit" variant="contained" color="primary" disabled={!isChannelChange()}>
                  {t("update")}
                </Button>
              ) : (
                <Button type="submit" variant="contained" color="primary" disabled={!isValidChannel()}>
                  {t("create")}
                </Button>
              )}
            </Grid>

          </Grid>  {/* END Row buttons */}
        </form>
      </Box>
    </Paper>
  );
}
